import React from 'react';
import Footer from './Footer';

const PrivacyPolicy: React.FC = () => {
  const handleGoHome = () => {
    window.location.hash = '#/';
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* App Title - Clickable */}
            <div className="flex items-center">
              <h1 
                className="text-2xl font-bold text-primary dark:text-primary cursor-pointer hover:text-primary-dark transition-colors"
                onClick={handleGoHome}
                title="Back to Get Deleto"
              >
                Get Deleto
              </h1>
            </div>
            
            {/* Theme Toggle */}
            <div className="flex items-center">
              <button
                onClick={() => {
                  const currentTheme = localStorage.getItem('theme') || 'light';
                  const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
                  localStorage.setItem('theme', newTheme);
                  document.documentElement.classList.toggle('dark', newTheme === 'dark');
                }}
                className="p-2 rounded-md transition-all duration-300 flex items-center justify-center overflow-hidden relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                style={{
                  background: (localStorage.getItem('theme') || 'light') === 'dark' 
                    ? 'linear-gradient(135deg, #4a90e2 0%, #2d3748 100%)' 
                    : 'linear-gradient(135deg, #f6ad55 0%, #f8f9fa 100%)',
                  width: '40px',
                  height: '40px'
                }}
                title={(localStorage.getItem('theme') || 'light') === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                aria-label={(localStorage.getItem('theme') || 'light') === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                <span className="absolute inset-0 flex items-center justify-center">
                  {(localStorage.getItem('theme') || 'light') === 'dark' ? (
                    <svg className="w-5 h-5 text-yellow-300 transform transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 2a6 6 0 110 12 6 6 0 010-12z" fillOpacity="0" />
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-indigo-900 transform transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Privacy Policy</h1>
          
          <div className="prose dark:prose-invert max-w-none">
            <p className="mb-4">Last updated: {new Date().toLocaleDateString()}</p>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">1. Introduction</h2>
            <p>Welcome to Get Deleto. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.</p>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">2. The Data We Collect</h2>
            <p>Get Deleto is designed with privacy in mind. We collect minimal data:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Local storage data: Your writing sessions are stored in your browser's local storage and are not transmitted to our servers.</li>
              <li>Anonymous usage data: We collect anonymous usage statistics to improve our service.</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">3. How We Use Your Data</h2>
            <p>We use the collected data solely to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Provide and maintain our service</li>
              <li>Improve and develop our website</li>
              <li>Monitor the usage of our service</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">4. Data Security</h2>
            <p>We prioritize the security of your data. Your writing content is stored locally on your device and is not transmitted to our servers unless you explicitly choose to use any cloud features we may offer in the future.</p>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">5. Third-Party Services</h2>
            <p>We may use third-party services that collect, monitor, and analyze data:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Analytics providers (such as Vercel Analytics)</li>
            </ul>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">6. Changes to This Privacy Policy</h2>
            <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
            
            <h2 className="text-xl font-semibold mt-6 mb-4">7. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, please contact us at getdeleto@gmail.com.</p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 